var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CREATE_INTERNAL_SIGNED_URL_HEADER, TRANSACTION_ID_HEADER, } from "@auvious/common";
import { defer, throwError } from "rxjs";
import { delay, retryWhen, take, tap, concatMap } from "rxjs/operators";
import { v4 as uuidv4 } from "uuid";
import { Asset } from "./model";
import log from "./log";
export class AssetService {
    /**
     * AssetService constructor.
     *
     * @param commonSource source for AuviousCommon
     * @param createInternalSignedUrls If true internal signed urls will be used, otherwise storage provider urls will be used. True by default, if not supplied true will be used.
     */
    constructor(commonSource, createInternalSignedUrls = true) {
        this.createInternalSignedUrls = createInternalSignedUrls;
        commonSource.subscribe((common) => {
            this.assetResource = common.apiResourceFactory("rtc-api/assets");
        });
    }
    /**
     * List all assets for an application.
     *
     * @param applicationId applicationId of assets to return
     * @returns list of assets
     */
    list(applicationId) {
        return __awaiter(this, void 0, void 0, function* () {
            const assets = (yield defer(() => this.assetResource.get({
                params: {
                    applicationId,
                },
            }))
                .pipe(retryWhen((errors) => errors.pipe(
            // log error message
            tap((val) => log.warn(`retrying`, val)), 
            // restart in 5 seconds
            delay(2000), take(3), concatMap((error) => throwError(error)))))
                .toPromise());
            return assets.map((asset) => new Asset(asset.assetId, asset.filename, asset.category, this.signedUrlProvider()));
        });
    }
    /**
     * Upload a file.
     *
     * @param blob the file to upload, as a Blob
     * @param applicationId which applicationId.
     * @param rolesAllowedAccess who to allow access, e.g ["agent","customer"]
     * @param category category of asset e.g. "background"
     * @param filename filename information
     * @returns
     */
    upload(blob, applicationId, rolesAllowedAccess = ["agent"], category, filename) {
        return __awaiter(this, void 0, void 0, function* () {
            const assetId = uuidv4();
            const transactionId = uuidv4();
            const formData = new FormData();
            formData.append("applicationId", applicationId);
            formData.append("rolesAllowedAccess", rolesAllowedAccess.join(","));
            if (category) {
                formData.append("category", category);
            }
            if (filename) {
                formData.append("filename", filename);
            }
            formData.append("file", blob);
            yield defer(() => this.assetResource.create(formData, {
                urlPostfix: assetId,
                headers: {
                    "content-type": "multipart/form-data",
                    [TRANSACTION_ID_HEADER]: transactionId,
                },
            }))
                .pipe(retryWhen((errors) => errors.pipe(
            // log error message
            tap((val) => log.warn(`retrying`, val)), 
            // restart in 5 seconds
            delay(2000), take(3), concatMap((error) => throwError(error)))))
                .toPromise();
            return new Asset(assetId, filename, category, this.signedUrlProvider());
        });
    }
    delete(assetId) {
        return __awaiter(this, void 0, void 0, function* () {
            const transactionId = uuidv4();
            yield defer(() => this.assetResource.delete({
                urlPostfix: assetId,
                headers: {
                    [TRANSACTION_ID_HEADER]: transactionId,
                },
            }))
                .pipe(retryWhen((errors) => errors.pipe(
            // log error message
            tap((val) => log.warn(`retrying`, val)), 
            // restart in 5 seconds
            delay(2000), take(3), concatMap((error) => throwError(error)))))
                .toPromise();
        });
    }
    signedUrlProvider() {
        return {
            get: (assetId) => __awaiter(this, void 0, void 0, function* () {
                const signedUrlResponse = yield this.assetResource.create(null, {
                    urlPostfix: `${assetId}/signedUrl`,
                    headers: {
                        [CREATE_INTERNAL_SIGNED_URL_HEADER]: this.createInternalSignedUrls,
                    },
                });
                return {
                    url: signedUrlResponse.url,
                    expires: new Date(Date.parse(signedUrlResponse.validUntil)),
                };
            }),
        };
    }
}
